import React from 'react';
import { Trash2, Download } from 'lucide-react';
import { Modal, Button, MessageList } from 'common/components';
import {
    formatLeadStatus,
    formatSMSStatus,
    formatSMSDisposition,
    getLeadStatusStyle,
    getSMSStatusStyle,
} from '../../utils';
import styles from './SMSDetailsModal.module.scss';
import type { SMSLead } from '../SMSTable';

export interface SMSDetailsModalProps {
    sms: SMSLead | null;
    transcriptMessages: SMSLead[];
    isOpen: boolean;
    onClose: () => void;
    onDelete: (params: { selectedCallId: string }) => void;
    onExportCSV: (params: { selectedCallId?: string }) => void;
    isLoading?: boolean;
}

export const SMSDetailsModal: React.FC<SMSDetailsModalProps> = ({
    sms,
    transcriptMessages,
    isOpen,
    onClose,
    onDelete,
    onExportCSV,
    isLoading = false,
}) => {
    if (!isOpen || !sms) return null;

    const messageItems = transcriptMessages.map((message) => ({
        smsTime: message.smsTime,
        smsDate: message.smsDate,
        speakerLabel: message.messageDirection === 'INBOUND' ? 'Customer' : 'Contact',
        text: message.messageContent || '(No message content)',
        direction: (message.messageDirection === 'INBOUND' ? 'inbound' : 'outbound') as 'inbound' | 'outbound',
        status: message.status,
    }));

    const footer = (
        <div className='flex justify-end gap-md'>
            <Button variant='outline-secondary' onClick={onClose}>
                Cancel
            </Button>
            <Button
                variant='primary'
                icon={Trash2}
                onClick={() => onDelete({ selectedCallId: sms.smsId })}
                disabled={isLoading}
            >
                Delete SMS
            </Button>
            <Button
                variant='primary'
                icon={Download}
                onClick={() => onExportCSV({ selectedCallId: sms.smsId })}
                disabled={isLoading}
            >
                Export CSV
            </Button>
        </div>
    );

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            size='md'
            showDefaultFooter={false}
            footer={footer}
            isLoadingOverlay={isLoading}
            title={<h2>SMS Conversation Transcript</h2>}
        >
            <div className={styles.callDetailsContainer}>
                <div className={styles.group}>
                    <div className={styles.title}>SMS Date</div>
                    <div className={styles.value}>
                        {sms.smsDate} {sms.smsTime}
                    </div>
                </div>
                <div className={styles.group}>
                    <div className={styles.title}>SMS Status</div>
                    <div>
                        <span style={getSMSStatusStyle(sms.status)}>{formatSMSStatus(sms.status)}</span>
                    </div>
                </div>
                <div className={styles.group}>
                    <div className={styles.title}>Party Status</div>
                    <div className={styles.value}>
                        <span style={getLeadStatusStyle(sms.crmStatus)}>{formatLeadStatus(sms.crmStatus)}</span>
                    </div>
                </div>
                <div className={styles.group}>
                    <div className={styles.title}>SMS Disposition</div>
                    <div className={styles.value}>{sms.disposition ? formatSMSDisposition(sms.disposition) : '-'}</div>
                </div>
                <div className={styles.group}>
                    <div className={styles.title}>Message Direction</div>
                    <div className={styles.value}>
                        {sms.messageDirection ? formatSMSStatus(sms.messageDirection) : '-'}
                    </div>
                </div>
                <div className={styles.group}>
                    <div className={styles.title}>CRM ID</div>
                    <div className={styles.value}>{sms.crmId || '-'}</div>
                </div>
                <div className={styles.group}>
                    <div className={styles.title}>Name</div>
                    <div className={styles.value}>{sms.name}</div>
                </div>
                <div className={styles.group}>
                    <div className={styles.title}>Number</div>
                    <div className={styles.value}>{sms.number}</div>
                </div>
                <div className={styles.group}>
                    <div className={styles.title}>SMS ID</div>
                    <div className={styles.value}>{sms.smsId || '-'}</div>
                </div>
            </div>

            <div className={styles.callDetailsContainer}>
                <div className={styles.group} style={{ gridColumn: '1 / -1' }}>
                    <div className={styles.messagesSectionTitle}>Message</div>
                    <MessageList items={messageItems} emptyMessage='No message content' />
                </div>
            </div>
        </Modal>
    );
};
